import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslocoModule } from '@jsverse/transloco';
import { InsightsService, TrackingEvent } from 'shared/domain';
import { FlowchartExportUtil } from 'shared/feature-flowchart';
import { BannerComponent, ButtonDirective, DialogRef, IconDirective } from 'shared/ui-common';

import { DIALOG_DATA } from '@treetop/shared/model';

export type ExportFormat = 'pdf' | 'html';

export type ExportDialogData = {
  guidelineUniqueId: string;
  guidelineTitle?: string;
  hasWorkflow?: boolean;
  format: ExportFormat;
  export: () => Promise<Blob> | undefined;
};

@Component({
  selector: 'tt-guideline-export-dialog',
  standalone: true,
  templateUrl: './export-dialog.component.html',
  styleUrls: ['./export-dialog.component.scss'],
  imports: [MatIconModule, TranslocoModule, MatProgressSpinnerModule, IconDirective, ButtonDirective, BannerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExportDialogComponent {
  public data = inject<ExportDialogData>(DIALOG_DATA);
  private dialogRef: DialogRef<void> = inject(DialogRef);
  private insightsService = inject(InsightsService);

  protected isGenerating = signal(true);
  protected blob = signal<Blob | null>(null);
  protected error = signal<string | null>(null);
  protected guidelineTitle = signal<string>(this.data.guidelineTitle || 'guideline');
  protected translationPrefix = this.data.format === 'html' ? 'HtmlExportDialog' : 'PdfExportDialog';

  constructor() {
    this.startExport();
  }

  private async startExport() {
    try {
      const blob = await this.data.export();
      if (!blob) {
        this.isGenerating.set(false);
        return;
      }
      this.blob.set(blob);
      this.isGenerating.set(false);
    } catch (err) {
      this.error.set(err instanceof Error ? err.message : 'Failed to generate export');
      this.isGenerating.set(false);
    }
  }

  protected downloadExport() {
    const blob = this.blob();
    if (!blob) {
      return;
    }

    const sanitizedFileName = FlowchartExportUtil.sanitizeFilename(this.guidelineTitle());
    const url = window.URL.createObjectURL(blob);
    const downloadLink = document.createElement('a');
    downloadLink.href = url;
    downloadLink.download = `${sanitizedFileName}.${this.data.format}`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    window.URL.revokeObjectURL(url);

    this.insightsService.trackEvent(this.data.format === 'html' ? TrackingEvent.GUIDELINE_HTML_EXPORTED : TrackingEvent.GUIDELINE_PDF_EXPORTED, {
      guidelineUniqueId: this.data.guidelineUniqueId,
      guidelineTitle: this.guidelineTitle(),
      exportType: 'guideline',
    });

    this.dialogRef.close();
  }

  protected onClose() {
    this.dialogRef.close();
  }
}
